// A table: a poker table whose board is the market. The live price runs across the felt between
// the line that pays and the line that busts; the room's players sit around the rail with their
// chips in; you sit at the bottom with yours, and the action bar below deals you in.

import { useEffect, useMemo, useState } from "react";
import { useConnection } from "wagmi";
import { useAccount, useOwner, useStreamStatus } from "../../state/account";
import { useInstruments } from "../../state/market";
import { useValuations } from "../../state/valuation";
import { useStore } from "../../state/store";
import { scalePlaces, toRaw, usd } from "../../lib/num";
import { useVol } from "../../odds/vol";
import { usePlays } from "../../play/book";
import { useSampledMark } from "../../play/hooks";
import { useQuote } from "../../play/quote";
import { useRoom, useSeating } from "../../play/room";
import { setSlip, slip as slipStore, type Call } from "../../play/slip";
import { chips } from "../../play/tape";
import { useWide } from "../../lib/media";
import { Arena, type Entry, type Level } from "./Arena";
import { Dock } from "./Dock";
import { Cards, Seat, Stack } from "./Seats";
import { useWorth } from "./Tickets";

export const COIN_NAME: Record<string, string> = { BTC: "Bitcoin", ETH: "Ether" };
export const coinName = (coin: string) => COIN_NAME[coin] ?? coin;

/** Where the other seats sit around the rail, phone and desktop. */
const SEATS_WIDE = ["t1", "t3", "r", "b3", "b1", "l"] as const;
const SEATS_TALL = ["t2", "tr", "br", "bl", "tl"] as const;

/** You, at the bottom of the table: your stack, and the chips in front of you. */
function Hero({ positionId, stake }: { positionId: string | null; stake: string }) {
  const { status } = useConnection();
  const account = useAccount();
  const valued = useValuations();
  const stream = useStreamStatus();
  const position = account?.positions.find((item) => item.positionId === positionId) ?? null;
  const worth = useWorth(position);
  const pending = toRaw(stake || "0", 18) ?? 0n;
  const seated = status === "connected";
  return (
    <div className={`seat seat-hero ${seated ? "" : "is-open"}`}>
      <span className="seat-avatar ring-hero">{position && <Cards size="lg" />}</span>
      <span className="seat-plate seat-plate-hero num">
        {seated ? (valued ? usd(valued.equity) : account ? usd(account.balance) : stream === "live" ? "—" : "…") : "Your seat"}
      </span>
      <span className="dealer" aria-hidden>
        D
      </span>
      {position ? (
        <span className="seat-bet">
          <Stack amount={BigInt(position.margin)} unit={10n ** 18n} label={worth ? worth.multiple : usd(position.margin)} />
        </span>
      ) : pending > 0n ? (
        <span className="seat-bet">
          <Stack amount={pending} unit={10n ** 18n} label={usd(pending, { places: 2, min: 0 })} ghost />
        </span>
      ) : null}
    </div>
  );
}

export function Game({ coin }: { coin: string }) {
  const wide = useWide();
  const instruments = useInstruments().filter((item) => !item.terminal);
  const instrument = instruments.find((item) => item.symbol === coin) ?? instruments[0];
  const owner = useOwner();
  const account = useAccount();
  const plays = usePlays();
  const state = useStore(slipStore);
  const mark = useSampledMark(instrument?.id);
  const sigma = useVol(instrument?.symbol);
  const [hover, setHover] = useState<Call | null>(null);
  const room = useRoom(instrument?.id, owner);
  const seats = useSeating(room.players, wide ? SEATS_WIDE.length : SEATS_TALL.length);

  useEffect(() => {
    if (instrument && state.coin !== instrument.symbol) setSlip({ coin: instrument.symbol });
  }, [instrument, state.coin]);

  const leverage = instrument ? Math.min(state.leverage, instrument.maxLeverage) : state.leverage;
  const base = instrument
    ? { owner, instrument, stake: state.stake, leverage, target: state.target ?? 2, clock: null, mark: mark?.raw ?? null, sigma }
    : null;
  const up = useQuote(base ? { ...base, call: "up" } : null, account);
  const down = useQuote(base ? { ...base, call: "down" } : null, account);

  const here = useMemo(() => (account?.positions ?? []).filter((position) => position.instrumentId === instrument?.id), [account, instrument?.id]);
  const focus = here[0] ?? null;
  const call = hover ?? state.call;
  const preview = call === "up" ? up : down;

  const levels: Level[] = [];
  const entries: Entry[] = [];
  if (focus) {
    const play = plays.find((item) => item.positionId === focus.positionId);
    levels.push({ at: Number(focus.bustPrice), kind: "bust", label: "Bust", strong: true, preview: false });
    if (play?.targetMark) levels.push({ at: Number(play.targetMark), kind: "win", label: play.targetPayout ? `Win ${usd(play.targetPayout)}` : "Win", strong: true, preview: false });
    entries.push({ t: focus.openedAtMs, at: Number(focus.entryPrice), strong: true });
  } else if (preview?.targetMark && preview.preview.bust && preview.targetPayout) {
    levels.push({ at: Number(preview.targetMark), kind: "win", label: `Win ${usd(preview.targetPayout)}`, strong: true, preview: true });
    levels.push({ at: Number(preview.preview.bust), kind: "bust", label: "Bust", strong: true, preview: true });
  }

  if (!instrument) {
    return (
      <main className="game">
        <div className="table is-loading">
          <span className="spinner" aria-hidden />
        </div>
      </main>
    );
  }

  const mine = focus ? BigInt(focus.margin) / 10n ** 10n : 0n;
  const pot = room.pot + mine;
  const at = wide ? SEATS_WIDE : SEATS_TALL;

  return (
    <main className="game">
      <section className="table" aria-label={`${coinName(instrument.symbol)} table`}>
        <div className="felt">
          <p className="pot">
            <span className="pot-chip" aria-hidden />
            <span className="pot-word">Pot</span>
            <span className="num">{chips(pot)}</span>
          </p>
          <div className="board">
            <Arena scene={{ instrumentId: instrument.id, coin: instrument.symbol, unit: 10 ** scalePlaces(instrument.priceScale), levels, entries }} />
          </div>
        </div>
        {seats.map((player, index) => (
          <Seat key={at[index]} player={player} at={at[index] ?? "t1"} />
        ))}
        <Hero positionId={focus?.positionId ?? null} stake={state.stake} />
      </section>
      <Dock instrument={instrument} quotes={{ up, down }} leverage={leverage} focus={focus} onPreview={setHover} />
    </main>
  );
}
